import type { RunResult } from '../types'
import { PROBLEMS } from './models'

// Beginner-mode presentation layer over the existing registry: emoji map
// labels, plain-number stage captions, slider ranges for the input fields,
// and one challenge per problem. Pure data — the backend and the technical
// registry in models.ts are untouched.

export interface SliderRange {
  min: number
  max: number
  step: number
}

export interface Challenge {
  text: string
  win: string
  check: (r: RunResult) => boolean
}

export interface BeginnerCfg {
  emoji: string
  question: string // ≤4 words, shown on the map node
  studyCaption: string // what the model studies, as a number
  testCaption: string // its test score, in plain words
  challenge: Challenge
  ranges: Record<string, SliderRange>
}

const pct = (r: RunResult) => r.probability ?? 0.5

export const BEGINNER: Record<string, BeginnerCfg> = {
  house: {
    emoji: '🏠',
    question: "What's it worth?",
    studyCaption: '1,460 sales',
    testCaption: '0.91 / 1 on hidden houses',
    challenge: {
      text: '🎯 make it worth over $500,000',
      win: '🎉 half a million! you just steered a model',
      check: (r) => (r.value ?? 0) > 500000,
    },
    ranges: {
      living_area: { min: 600, max: 4000, step: 50 },
      overall_qual: { min: 1, max: 10, step: 1 },
      year_built: { min: 1900, max: 2024, step: 1 },
      garage_cars: { min: 0, max: 4, step: 1 },
    },
  },
  churn: {
    emoji: '📉',
    question: 'Who will cancel?',
    studyCaption: '7,043 customers',
    testCaption: '87 / 100 ranked right',
    challenge: {
      text: '🎯 save this customer — risk under 20%',
      win: '🎉 saved! that slider move = a retention offer',
      check: (r) => pct(r) < 0.2,
    },
    ranges: {
      tenure: { min: 0, max: 72, step: 1 },
      monthly_charges: { min: 20, max: 120, step: 1 },
      support_calls: { min: 0, max: 10, step: 1 },
    },
  },
  fraud: {
    emoji: '💳',
    question: 'Which payment is a thief?',
    studyCaption: '284,807 payments',
    testCaption: 'catches 82 of 100 frauds',
    challenge: {
      text: '🎯 make this payment look legit — risk under 20%',
      win: '🎉 cleared! now you know what makes fraud look like fraud',
      check: (r) => pct(r) < 0.2,
    },
    ranges: {
      amount: { min: 1, max: 5000, step: 10 },
      txn_hour: { min: 0, max: 23, step: 1 },
      velocity: { min: 0, max: 20, step: 1 },
    },
  },
  equipment: {
    emoji: '⚙️',
    question: 'Will it break down?',
    studyCaption: '10,000 machines',
    testCaption: '0.91 / 1 on hidden machines',
    challenge: {
      text: '🎯 keep it alive — failure risk under 20%',
      win: '🎉 machine saved — maintenance can wait',
      check: (r) => pct(r) < 0.2,
    },
    ranges: {
      air_temp: { min: 295, max: 305, step: 1 },
      torque: { min: 10, max: 80, step: 1 },
      rpm: { min: 1200, max: 2900, step: 20 },
      tool_wear: { min: 0, max: 250, step: 5 },
    },
  },
  intrusion: {
    emoji: '🛡️',
    question: 'Is this an attack?',
    studyCaption: '125,973 connections',
    testCaption: '94% right on hidden traffic',
    challenge: {
      text: '🎯 make this traffic look normal — attack risk under 10%',
      win: '🎉 all clear! you found exactly what the alarm watches',
      check: (r) => pct(r) < 0.1,
    },
    ranges: {
      src_bytes: { min: 0, max: 10000, step: 100 },
      dst_bytes: { min: 0, max: 10000, step: 100 },
      count: { min: 0, max: 511, step: 1 },
    },
  },
  demand: {
    emoji: '📦',
    question: 'How many will sell?',
    studyCaption: '913,000 sales records',
    testCaption: '~9% off on hidden weeks',
    challenge: {
      text: '🎯 plan a big week — forecast over 450 units',
      win: '🎉 shelves stocked! that was inventory forecasting',
      check: (r) => (r.value ?? 0) > 450,
    },
    ranges: {
      last_week: { min: 0, max: 900, step: 10 },
      lag_52: { min: 0, max: 900, step: 10 },
      week_of_year: { min: 1, max: 52, step: 1 },
    },
  },
  recommend: {
    emoji: '🍿',
    question: 'What to watch next?',
    studyCaption: '100,000 ratings',
    testCaption: '±0.9★ on hidden ratings',
    challenge: {
      text: '🎯 find a great match — over 4.0★',
      win: '🎉 matched! that is how Netflix guesses your taste',
      check: (r) => (r.value ?? 0) > 4.0,
    },
    ranges: {
      user_avg: { min: 1, max: 5, step: 0.1 },
      item_avg: { min: 1, max: 5, step: 0.1 },
      genre_match: { min: 0, max: 1, step: 0.05 },
      n_rated: { min: 1, max: 500, step: 5 },
    },
  },
  credit: {
    emoji: '🏦',
    question: 'Approve this loan?',
    studyCaption: '1,000 loans',
    testCaption: '79 / 100 ranked right',
    challenge: {
      text: '🎯 get this loan approved — default risk under 20%',
      win: '🎉 approved! banks run this exact trade-off all day',
      check: (r) => pct(r) < 0.2,
    },
    ranges: {
      income: { min: 500, max: 10000, step: 100 },
      loan_amount: { min: 1000, max: 60000, step: 500 },
      employment_years: { min: 0, max: 30, step: 1 },
    },
  },
  crop: {
    emoji: '🌿',
    question: 'What disease is this?',
    studyCaption: '54,305 leaf photos',
    testCaption: '97 / 100 leaves right',
    challenge: {
      text: '🎯 find the healthy leaf',
      win: '🌿 diagnosed! same 3 steps — the examples are photos',
      check: (r) => r.label === 'Healthy',
    },
    ranges: {},
  },
  energy: {
    emoji: '⚡',
    question: 'How much power tonight?',
    studyCaption: '145,366 hours',
    testCaption: '~4% off on hidden hours',
    challenge: {
      text: '🎯 predict a heat-wave rush hour — over 55,000 MW',
      win: '🎉 grid balanced! that forecast keeps lights on',
      check: (r) => (r.value ?? 0) > 55000,
    },
    ranges: {
      temp: { min: -10, max: 45, step: 1 },
      hour: { min: 0, max: 23, step: 1 },
      load_lag_24: { min: 20000, max: 45000, step: 250 },
    },
  },
}

// map display order — mirrors the 10 problems in mlmodels.md, 1 → 10
export const MAP_ORDER = [
  'churn',
  'fraud',
  'equipment',
  'intrusion',
  'demand',
  'recommend',
  'house',
  'credit',
  'crop',
  'energy',
]

// ---- the model axis: the same registry grouped by model family ----
export interface ModelFamily {
  name: string
  summary: string
  members: { problemId: string; algoId: string }[]
}

export const MODEL_FAMILIES: ModelFamily[] = (() => {
  const byName = new Map<string, ModelFamily>()
  for (const problemId of MAP_ORDER) {
    const problem = PROBLEMS.find((p) => p.id === problemId)
    if (!problem) continue
    for (const algo of problem.algorithms) {
      const family = byName.get(algo.name) ?? { name: algo.name, summary: algo.summary, members: [] }
      family.members.push({ problemId, algoId: algo.id })
      byName.set(algo.name, family)
    }
  }
  // most versatile first (XGBoost with 6 problems leads the column)
  return Array.from(byName.values()).sort((a, b) => b.members.length - a.members.length)
})()
